import { classifyFailure } from "@lafayette/api-client";
import type { ItineraryRefusal } from "@lafayette/schemas";

import { describeStaffFailure, NEUTRAL_FORBIDDEN_MESSAGE } from "../failure-words";

// How the itinerary screens tell staff what went wrong. The rules match the customer memory pages:
//   1. No title, note, reason or id goes into a message. The API sends none: a refusal holds a rule code, the NAME of a field and,
//      for a clock gap, the first valid local time.
//   2. A refusal for access and a missing target read the same (the API answers both 403).
//   3. A conflict says in plain words that the plan changed, that the change was NOT saved, and that the page was reloaded.

export type ItineraryFailureKind =
  | "conflict"
  | "needs_reason"
  | "time_gap"
  | "time_ambiguous"
  | "invalid"
  | "refused"
  | "forbidden"
  | "unauthenticated"
  | "rate_limited"
  | "unavailable"
  | "network"
  | "response"
  | "unknown";

export type ItineraryFailure = Readonly<{
  kind: ItineraryFailureKind;
  message: string;
  /** The same command may be sent again with the SAME idempotency key: it may or may not have run. */
  retryable: boolean;
  /** The plan the page shows is out of date: it must be read again. */
  reload: boolean;
  code: ItineraryRefusal["code"] | null;
  /** For `time_gap`: the first valid local time after the gap, `YYYY-MM-DDTHH:MM`. */
  suggestion: string | null;
  /** For `invalid`: the NAME of the refused field. */
  field: string | null;
  requestId: string | null;
}>;

const base = (kind: ItineraryFailureKind, message: string): ItineraryFailure => ({
  kind,
  message,
  retryable: false,
  reload: false,
  code: null,
  suggestion: null,
  field: null,
  requestId: null,
});

const CHANGED = "The plan was reloaded. Check it, then make the change again.";

function refusalFailure(refusal: ItineraryRefusal): ItineraryFailure {
  const withCode = (failure: ItineraryFailure): ItineraryFailure => ({
    ...failure,
    code: refusal.code,
  });
  switch (refusal.code) {
    case "itinerary.version_conflict":
      return withCode({
        ...base(
          "conflict",
          `This plan changed while you were editing. Your change was not saved. ${CHANGED}`,
        ),
        reload: true,
      });
    case "itinerary.base_not_active":
      return withCode({
        ...base(
          "conflict",
          `The plan you edited is no longer the current plan. Your change was not saved. ${CHANGED}`,
        ),
        reload: true,
      });
    case "itinerary.not_mutable":
      return withCode({
        ...base(
          "conflict",
          `This plan is no longer a draft, so it cannot be edited in place. Your change was not saved. ${CHANGED}`,
        ),
        reload: true,
      });
    case "itinerary.active_exists":
      return withCode({
        ...base(
          "conflict",
          `This trip already has a current plan. Edit that plan instead. ${CHANGED}`,
        ),
        reload: true,
      });
    case "itinerary.item_not_found":
    case "itinerary.section_not_found":
      return withCode({
        ...base(
          "conflict",
          `An item or day in your change no longer exists. Your change was not saved. ${CHANGED}`,
        ),
        reload: true,
      });
    case "itinerary.trip_closed":
      return withCode(base("refused", "The trip is closed, so its plan cannot change."));
    case "itinerary.requires_confirmation":
      return withCode(
        base(
          "needs_reason",
          "This change touches something the customer holds: a fixed item, an item with a request, or a payment. Add a reason to apply it as staff, or save it as a proposal.",
        ),
      );
    case "itinerary.item_linked":
      return withCode(
        base(
          "needs_reason",
          "A request or a payment names this item. Removing it needs a staff reason.",
        ),
      );
    case "itinerary.invalid_structure":
      return withCode(
        base(
          "refused",
          "The plan would have a blocking problem (for example two items that overlap, or a travel time longer than the gap). Nothing was saved. Fix the times, then try again.",
        ),
      );
    case "itinerary.time_not_existent":
      return {
        ...base(
          "time_gap",
          "This clock time does not exist in that zone: the clocks jump forward over it.",
        ),
        code: refusal.code,
        suggestion: refusal.suggestion,
      };
    case "itinerary.time_ambiguous":
      return withCode(
        base(
          "time_ambiguous",
          "This clock time happens twice in that zone: the clocks go back. Choose which one you mean.",
        ),
      );
    case "itinerary.invalid_input":
      return {
        ...base(
          "invalid",
          refusal.field === null
            ? "The server did not accept this change. Check the fields."
            : `The server did not accept the field "${refusal.field}". Check it and try again.`,
        ),
        code: refusal.code,
        field: refusal.field,
      };
  }
}

const COLLEAGUE = "Something went wrong. Try again, or ask a colleague.";
const BUSY = {
  kind: "unavailable",
  message: "The service is busy or down. Try again in a moment.",
  retryable: true,
} as const;
const UNKNOWN = { kind: "unknown", message: COLLEAGUE, retryable: false } as const;
const INVALID = "The server did not accept this change. Check the fields.";

export function describeItineraryError(error: unknown): ItineraryFailure {
  // A refusal of a rule has its own words per code. Everything else reads the shared shape.
  const given = classifyFailure(error);
  if (given.detail.itineraryRefusal !== null) {
    return { ...refusalFailure(given.detail.itineraryRefusal), requestId: given.requestId };
  }
  const { kind, message, retryable, reload, requestId } =
    describeStaffFailure<ItineraryFailureKind>(error, {
      forbidden: NEUTRAL_FORBIDDEN_MESSAGE,
      // The same Idempotency-Key is still running on the server: wait and send the same key again.
      conflict: (failure) =>
        failure.inFlight
          ? {
              kind: "unavailable",
              message: "The same action is still running. Wait a moment, then try again.",
              retryable: true,
              reload: false,
            }
          : `The plan changed. Your change was not saved. ${CHANGED}`,
      on: {
        network: "The service could not be reached. Check the connection, then try again.",
        response: "The service answered in a way this page does not understand. Reload the page.",
        unauthenticated: "Your session ended. Sign in again.",
        rate_limited: "Too many requests. Wait a moment, then try again.",
        // The itinerary screens do not tell a 400, a 413 and a 422 apart.
        bad_request: { kind: "invalid", message: INVALID },
        too_large: { kind: "invalid", message: INVALID },
        invalid: INVALID,
        server: BUSY,
        unavailable: BUSY,
        other_status: (failure) =>
          failure.status !== null && failure.status >= 500 ? BUSY : UNKNOWN,
        timeout: UNKNOWN,
        upload: UNKNOWN,
        other: UNKNOWN,
      },
    });
  return { ...base(kind, message), retryable, reload, requestId };
}
